// Saisie ou modification d'un frais ou d'un débours, depuis le dossier ou l'onglet Frais & débours.
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Segments } from '../ui/ListToolbar';
import { cn } from '../../lib/cn';
import { formatEuros } from '../../services/billing/billingService';
import { expenseReceiptUrl, type DossierExpense } from '../../services/billing/expenseService';
import { useSaveExpense, useUploadExpenseReceipt } from '../../hooks/queries/useExpenses';
import { useDossiers } from '../../hooks/queries/useDossiers';
import { expenseAmounts, validateExpense, type ExpenseErrors, type ExpenseNature } from '../../lib/billing/expenses';
import { formatVatRate, VAT_RATE_OPTIONS } from '../../lib/billing/lineTotals';

const HINTS: Record<ExpenseNature, string> = {
  debours: 'Somme avancée au nom et pour le compte du client. Refacturée à l’euro près, sans TVA, sur une facture de débours.',
  frais: 'Dépense du cabinet refacturée au client, avec TVA, sur une facture d’honoraires.',
};

const SELECT_CLASS =
  'w-full h-10 px-3 text-sm rounded-cime border border-neutral-300 bg-white text-neutral-900 hover:border-neutral-400 focus:outline-none focus:ring-2 focus:ring-cime-primary focus:border-transparent disabled:bg-neutral-50 disabled:text-neutral-400';

const today = () => new Date().toISOString().slice(0, 10);

interface Props {
  open: boolean;
  onClose: () => void;
  /** Dossier imposé (saisie depuis le dossier). Sans lui, la fenêtre propose la liste. */
  dossierId?: string;
  /** Ligne à modifier. */
  expense?: DossierExpense | null;
}

export function ExpenseFormDialog({ open, onClose, dossierId, expense }: Props) {
  const save = useSaveExpense();
  const upload = useUploadExpenseReceipt();
  const { data: dossiers } = useDossiers();
  const fileInput = useRef<HTMLInputElement>(null);

  const [nature, setNature] = useState<ExpenseNature>('debours');
  const [pickedDossier, setPickedDossier] = useState('');
  const [label, setLabel] = useState('');
  const [supplier, setSupplier] = useState('');
  const [pieceNumber, setPieceNumber] = useState('');
  const [pieceDate, setPieceDate] = useState(today());
  const [amount, setAmount] = useState('');
  const [vatRate, setVatRate] = useState(20);
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  // Les erreurs s'affichent après une première tentative, puis suivent la saisie.
  const [tried, setTried] = useState(false);

  // Remplit la fenêtre à chaque ouverture (nouvelle ligne ou modification).
  useEffect(() => {
    if (!open) return;
    setNature((expense?.nature as ExpenseNature) ?? 'debours');
    setPickedDossier(expense?.dossier_id ?? dossierId ?? '');
    setLabel(expense?.label ?? '');
    setSupplier(expense?.supplier ?? '');
    setPieceNumber(expense?.piece_number ?? '');
    setPieceDate(expense?.piece_date ?? today());
    setAmount(
      expense ? String(expense.nature === 'debours' ? expense.amount_ttc : expense.amount_ht).replace('.', ',') : '',
    );
    setVatRate(expense && expense.nature === 'frais' ? Number(expense.vat_rate) : 20);
    setFile(null);
    setTried(false);
  }, [open, expense, dossierId]);

  const effectiveDossier = dossierId ?? pickedDossier;
  const amountValue = Number(amount.replace(/\s/g, '').replace(',', '.')) || 0;
  const computed = useMemo(() => expenseAmounts({ nature, amount: amountValue, vat_rate: vatRate }), [nature, amountValue, vatRate]);
  const busy = save.isPending || upload.isPending;
  const currentErrors = useMemo(() => {
    const found: ExpenseErrors & { dossier?: string } = validateExpense({
      nature, piece_date: pieceDate, label, supplier, piece_number: pieceNumber, amount: amountValue,
    });
    if (!effectiveDossier) found.dossier = 'Choisissez un dossier.';
    return found;
  }, [nature, pieceDate, label, supplier, pieceNumber, amountValue, effectiveDossier]);
  const errors = tried ? currentErrors : {};

  async function submit() {
    setTried(true);
    if (Object.keys(currentErrors).length > 0) return;
    try {
      const saved = await save.mutateAsync({
        id: expense?.id,
        input: {
          dossier_id: effectiveDossier,
          nature,
          piece_date: pieceDate,
          label,
          supplier,
          piece_number: pieceNumber,
          amount: amountValue,
          vat_rate: vatRate,
        },
      });
      if (file) await upload.mutateAsync({ expense: saved, file });
      toast.success(expense ? 'Ligne modifiée' : nature === 'debours' ? 'Débours enregistré' : 'Frais enregistré');
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Enregistrement impossible');
    }
  }

  async function openReceipt() {
    if (!expense?.receipt_path) return;
    try {
      window.open(await expenseReceiptUrl(expense.receipt_path), '_blank', 'noopener');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Justificatif introuvable');
    }
  }

  function pickFile(list: FileList | null) {
    const f = list?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error('Le justificatif dépasse 10 Mo.');
      return;
    }
    setFile(f);
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="sm:max-w-lg bg-white rounded-cime border-neutral-200">
        <DialogHeader>
          <DialogTitle className="text-cime-text-primary">{expense ? 'Modifier la ligne' : 'Frais ou débours'}</DialogTitle>
          <DialogDescription className="text-cime-text-muted">{HINTS[nature]}</DialogDescription>
        </DialogHeader>

        <Segments
          label="Nature"
          value={nature}
          onChange={(v) => setNature(v as ExpenseNature)}
          options={[
            { value: 'debours', label: 'Débours' },
            { value: 'frais', label: 'Frais' },
          ]}
        />

        <div className="grid grid-cols-2 gap-x-3.5 gap-y-3">
          {!dossierId && (
            <div className="col-span-2 flex flex-col gap-1">
              <label htmlFor="exp-dossier" className="text-sm font-medium text-neutral-700">Dossier</label>
              <select
                id="exp-dossier"
                className={cn(SELECT_CLASS, errors.dossier && 'border-red-500')}
                value={pickedDossier}
                onChange={(e) => setPickedDossier(e.target.value)}
                disabled={!!expense}
              >
                <option value="">Choisir un dossier</option>
                {(dossiers ?? []).map((d) => (
                  <option key={d.id} value={d.id}>{d.number} — {d.title}</option>
                ))}
              </select>
              {errors.dossier ? <p className="text-xs text-red-600">{errors.dossier}</p> : null}
            </div>
          )}
          {nature === 'debours' ? (
            <>
              <Input id="exp-supplier" label="Fournisseur" value={supplier} onChange={(e) => setSupplier(e.target.value)} error={errors.supplier} placeholder="SCP Girard & Associés" />
              <Input id="exp-piece" label="N° de pièce" value={pieceNumber} onChange={(e) => setPieceNumber(e.target.value)} error={errors.piece_number} placeholder="F-2026-118" />
            </>
          ) : null}
          <div className="col-span-2">
            <Input
              id="exp-label"
              label={nature === 'debours' ? 'Libellé (facultatif)' : 'Libellé'}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              error={errors.label}
              placeholder={nature === 'debours' ? 'Signification de l’assignation' : 'Déplacement au tribunal de Rennes'}
            />
          </div>
          <Input id="exp-date" type="date" label="Date de la pièce" value={pieceDate} onChange={(e) => setPieceDate(e.target.value)} error={errors.piece_date} />
          <Input
            id="exp-amount"
            label={nature === 'debours' ? 'Montant TTC' : 'Montant HT'}
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            error={errors.amount}
            placeholder="0,00"
            className="text-right tabular-nums"
          />
          <div className="flex flex-col gap-1">
            <label htmlFor="exp-vat" className="text-sm font-medium text-neutral-700">TVA</label>
            <select
              id="exp-vat"
              className={SELECT_CLASS}
              value={nature === 'debours' ? 'none' : String(vatRate)}
              onChange={(e) => setVatRate(Number(e.target.value))}
              disabled={nature === 'debours'}
            >
              {nature === 'debours' ? <option value="none">Sans TVA</option> : null}
              {VAT_RATE_OPTIONS.map((r) => (
                <option key={r} value={String(r)}>{formatVatRate(r)}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col justify-end pb-2 text-right text-sm text-cime-text-muted">
            {nature === 'frais' && amountValue > 0 ? <span>TTC <b className="tabular-nums text-cime-text-primary">{formatEuros(computed.amount_ttc)}</b></span> : null}
          </div>

          <div className="col-span-2 flex flex-col gap-1">
            <span className="text-sm font-medium text-neutral-700">Justificatif</span>
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => { e.preventDefault(); setDragging(false); pickFile(e.dataTransfer.files); }}
              className={cn(
                'flex items-center gap-2.5 rounded-[10px] border border-dashed px-3.5 py-3 text-left text-[13px] transition-colors',
                dragging ? 'border-cime-primary bg-[#e8f0ec] text-cime-primary' : 'border-neutral-300 text-cime-text-muted hover:border-neutral-400',
              )}
            >
              <svg viewBox="0 0 448 512" className="h-3.5 w-3.5 shrink-0" fill="currentColor" aria-hidden>
                <path d="M364.2 83.8c-24.4-24.4-64-24.4-88.4 0l-184 184c-42.1 42.1-42.1 110.3 0 152.4s110.3 42.1 152.4 0l152-152c10.9-10.9 28.7-10.9 39.6 0s10.9 28.7 0 39.6l-152 152c-64 64-167.6 64-231.6 0s-64-167.6 0-231.6l184-184c46.3-46.3 121.3-46.3 167.6 0s46.3 121.3 0 167.6l-176 176c-28.6 28.6-75 28.6-103.6 0s-28.6-75 0-103.6l144-144c10.9-10.9 28.7-10.9 39.6 0s10.9 28.7 0 39.6l-144 144c-6.7 6.7-6.7 17.7 0 24.4s17.7 6.7 24.4 0l176-176c24.4-24.4 24.4-64 0-88.4z" />
              </svg>
              <span className="min-w-0 truncate">
                {file
                  ? file.name
                  : expense?.receipt_name
                    ? `${expense.receipt_name} — cliquer pour remplacer`
                    : 'Glissez la facture du prestataire ici, ou choisissez un fichier (PDF ou photo).'}
              </span>
            </button>
            <input
              ref={fileInput}
              type="file"
              accept="application/pdf,image/jpeg,image/png,image/webp,image/heic"
              className="hidden"
              onChange={(e) => pickFile(e.target.files)}
            />
            {expense?.receipt_path && !file ? (
              <button type="button" onClick={openReceipt} className="self-start text-xs text-cime-primary underline underline-offset-2">
                Voir le justificatif actuel
              </button>
            ) : null}
            {nature === 'debours' && !file && !expense?.receipt_path ? (
              <p className="text-xs text-neutral-400">Vous pourrez l’ajouter plus tard. La facture de débours ne se finalise pas sans lui.</p>
            ) : null}
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" disabled={busy} onClick={onClose}>Annuler</Button>
          <Button type="button" variant="primary" isLoading={busy} onClick={submit}>Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
